import type { TFunction } from 'i18next';

/**
 * The Autopilot cadence, said the way an owner would.
 *
 * The service carries the interval in minutes (`interval_minutes`: 2880 in
 * production, 5 in a test environment). The panel opens its copy with it, so
 * it is read in the unit that fits: a day or more in days, an hour or more in
 * hours, anything shorter in minutes, rounded to a whole count that is never
 * below one. Each phrase is its own literal, singular apart from plural, so
 * every key is extracted and translated on its own.
 */

export type CadenceUnit = 'day' | 'hour' | 'minute';

export type Cadence = { unit: CadenceUnit; n: number };

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

export const cadenceFromMinutes = (minutes: number): Cadence => {
  const unit: CadenceUnit = minutes >= MINUTES_PER_DAY ? 'day' : minutes >= MINUTES_PER_HOUR ? 'hour' : 'minute';
  const per = unit === 'day' ? MINUTES_PER_DAY : unit === 'hour' ? MINUTES_PER_HOUR : 1;
  return { unit, n: Math.max(1, Math.round(minutes / per)) };
};

/** "About every 2 days", "About every hour", "About every 5 minutes": the sentence-initial phrase. */
export const cadenceCopy = (t: TFunction, minutes: number): string => {
  const { unit, n } = cadenceFromMinutes(minutes);
  if (unit === 'day') return n === 1 ? t('About every day') : t('About every {{n}} days', { n });
  if (unit === 'hour') return n === 1 ? t('About every hour') : t('About every {{n}} hours', { n });
  return n === 1 ? t('About every minute') : t('About every {{n}} minutes', { n });
};
